// Brain Faculties Evaluation — wire shapes emitted by the Python brain-eval core (camelCase JSON).
// Mirror of the Brain Faculties Evaluation. The Python dataclasses are authoritative for the
// running core; these are the typed view consumers (the brain librarian, future sinks) read.

export type EvalDecision = "promote" | "reject" | "needs_review";
export type EvalJudgeStatus = "ok" | "skipped" | "unavailable";

export interface EvalFinding {
  code: string;
  severity: "hard" | "soft";
  message: string;
  locus?: string | null;
}

export interface EvalJudgeResult {
  overallScore: number;
  critique: string;
  model: string;
  flags?: {
    behavioralRisk: "pass" | "fail";
    collision: boolean;
    /** Per-tag provenance: true = the tag was absent/malformed in the critique and the
     *  effective value above is a default. Any defaulted tag forces needs_review. */
    defaulted?: { safety: boolean; collision: boolean };
  } | null;
  keyPoints?: string[] | null;
}

export interface Verdict {
  candidatePath: string;
  decision: EvalDecision;
  rationale: string;
  findings: EvalFinding[];
  judge?: EvalJudgeResult | null;
  judgeStatus: EvalJudgeStatus;
  evaluatedAt: string;
  evalVersion: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Exam core (Brain Referee), reconciled field-by-field.
// Additive beside the shipped `Verdict` above — do NOT drift that shape.
// AUTHORSHIP NOTE: these are produced by the TS adapter + the Python orchestrator;
// there is NO Python dataclass mirror of ArmSpec/RunResult/ExamVerdict beyond the
// orchestrator's internal load/serialize helpers. The TS↔Python boundary stays at
// the judge layer. Do not add Python mirrors.
// ─────────────────────────────────────────────────────────────────────────────

export type ArmState =
  | "off"                    // skeleton (loader ON, no content) — the MemBench humility check
  | "live"                   // a content COPY of the worker's current production brain
  | { pinned: string }       // git ref (commit SHA / tag) — dream-delta + skill-abstraction arms
  | { imported: string };    // benchmark-history key — fixture-filling comes later; this only routes it (stub)

export interface ArmSpec {
  brain: string;             // adapter key, NOT a repo path — the adapter carries the runtime
  state: ArmState;
  profile?: string;          // adapter runtime/intervention profile; absent = default.
                             // For tournament arms this carries the declared intervention boundary
                             // (loader/tooling), which content-isolation exams (impact/dream_delta) leave absent.
}

export interface RunAssertion {
  text: string;
  passed: boolean;
  evidence: string;          // the judge's cited transcript span
  tagDefaulted?: boolean;    // PASS/FAIL tag absent/malformed -> forces inconclusive (mirrors flags.defaulted)
  oracleTier?: "state_check" | "judge";   // copied from the TaskAssertion at grade-flatten time
}

export interface MemoryReadEvent {
  path?: string;             // gen_ai-span path-granularity unconfirmed -> optional
  spanRef?: string;          // span/tool-call correlation handle
  tool?:
    | "brain_read_indexes"
    | "brain_read_file"
    | "get_file_contents"
    | "search_code";        // gateway tools plus legacy GitHub MCP names
  query?: string;            // search_code arg; result-path attribution best-effort/unverified
}

// ── read-vs-apply diagnostic ──
export type ReadVsApply = "never_read" | "index_seen_not_opened" | "read_not_applied" | "degraded";
export interface ReadDiagnostic {
  assertionId: string;
  recallTarget?: string;     // the expected brain file path for this assertion
  label: ReadVsApply;
}

export interface ConsolidationOutcome {
  // ran = a dream executed; no_consolidation = a MEASURED brain property; not_runnable VOIDS the episode.
  status: "ran" | "no_consolidation" | "not_runnable";
  receipt?: string;          // human-readable summary
  producedSha?: string;      // status:"ran" — the post-consolidation content SHA (a new pinned ref)
  reason?: string;           // status:"not_runnable" — WHY it could not run (voids the episode)
}

export interface RunCost {
  tokens: number | null;     // null when the provider surfaced no usage; reconcile later via responseId
  promptTokens?: number | null;
  completionTokens?: number | null;
  durationMs: number;        // always measurable client-side
}

export interface RunResult {                 // one task/episode × one arm × one rep
  taskId: string;
  arm: ArmSpec;
  rep: number;
  runId: string;             // deterministic from (taskId · arm-digest · rep) — redaction + flip key
  assertions: RunAssertion[];
  implicitClaims?: { claim: string; verified: boolean }[];   // E1-feed shape; home keeps {type, evidence} too
  observe?: MemoryReadEvent[];               // Fills the producer; v1 = []
  consolidation?: ConsolidationOutcome;      // episodes only
  responseId?: string;       // CollectedResponse.responseId — ledger correlation key
  conversationId?: string;   // seam E2: probe conv id for observe() correlation
  readDiagnostics?: ReadDiagnostic[];        // nested in runs[] → sealed redaction covers it
  cost: RunCost;
  runError?: { reason: "content_filter" | "auth" | "not_found" | "rate_limit" | "transient" | "void" | "unknown"; message: string };
}

export type ExamType =
  | "impact" | "dream_delta" | "tournament"
  | "continuity" | "recall_pr" | "forgetting" | "skill_abstraction";  // values reserved

export type ExamRunStatus = "ok" | "partial" | "inconclusive_judge_error";
export type ExamVerdictValue = "improved" | "no_detectable_change" | "regressed";

/** One grader suite-critique line, attributed to the run's task AT THE SOURCE.
 *  `text` may quote the sealed assertion TEXT verbatim and name NO taskId — so redaction MUST drop
 *  by `taskId`, not by substring-matching the taskId inside `text` (the Law-1 sealed-text leak fix).
 *  `taskId` is "" for suite-level entries that belong to no single task (e.g. a judge-outage note). */
export interface SuiteCritiqueEntry {
  taskId: string;
  text: string;
}

// ── precision/recall metrics ──
export interface RecallMetrics {
  precisionInterference: number;  // TP/(TP+FP) — computed ONLY over stem-paired interference negatives
  recall: number;                 // TP/(TP+FN)
  staleWithholdRate: number;      // stale-fact negatives correctly withheld (own rate, not pooled into precision)
  privacyWithholdRate: number;    // privacy negatives correctly withheld (own rate)
  tp: number;
  fp: number;
  fn: number;
  tn: number;
  nPositive: number;              // should-recall assertions
  nNegInterference: number;       // stem-paired interference negatives
  nNegStale: number;
  nNegPrivacy: number;
}

export interface ArmAggregate {
  arm: ArmSpec;
  passRate: number;
  stddev: number;
  n: number;                 // run count behind the aggregate (lets the redacted feed report power)
  runs: RunResult[];         // FULL detail — present only in the home copy
  recallMetrics?: RecallMetrics;  // recall_pr only; aggregate → feed-safe
}

export interface ArmComparison {             // one per arm pair; tournament emits the full pairwise set
  baseline: ArmSpec;
  candidate: ArmSpec;
  flips: { taskId: string; direction: "gain" | "loss"; lowConfidence?: boolean }[];
  nDiscordant: number;       // d = stable gains + stable losses (the inferential count)
  nGain: number;
  nLoss: number;
  pValue: number;            // exact two-sided sign test (= exact McNemar on discordant pairs)
  significant: boolean;      // pValue <= alpha AND nDiscordant >= 6
  delta: number;             // pooled-mean pass-rate delta — DESCRIPTIVE ONLY, not the verdict
}

export interface StatTestConfig {            // stamped into every verdict for reproducibility
  test: "exact_sign";
  alpha: number;             // default 0.05
  targetPower: number;       // default 0.80
  sided: "two";              // two-sided default; one-sided is opt-in + pre-registered
  repAggregation: "majority_vote";
  significanceFloorDiscordant: 6;            // computed constant: min d for any two-sided p<=0.05
}

export interface ExamVerdict {
  examType: ExamType;
  taskSetVersion: string;    // versions the TASKS (content)
  rubricVersion: string;     // versions the assertions/criteria text
  evalVersion: string;       // versions the GRADER PROSE — the exam constant (= EXAM_EVAL_VERSION)
  statTest: StatTestConfig;
  runStatus: ExamRunStatus;
  arms: ArmAggregate[];
  comparisons?: ArmComparison[];
  verdict: ExamVerdictValue | null;          // null IFF runStatus === "inconclusive_judge_error" (the firewall)
  powerNote: string;         // tasks, n, MDE per the locked test
  suiteCritique: SuiteCritiqueEntry[];       // the grader's eval_feedback + the data-driven flakiness
                                             // taxonomy, each entry attributed to its taskId at the
                                             // source so the feed can redact sealed entries BY ID
  evaluatedAt: string;       // ISO-Z, matching Verdict.evaluatedAt
  loaderSha?: string;        // brain-loader.md hash pinned for this run (provenance)
  judgeDeployment?: string;  // the DISTINCT grading deployment used (independence provenance)
}

// ── Sealed-task redaction variant (E1-facing feed) ──

export interface RedactedArmAggregate {
  arm: ArmSpec;
  passRate: number;
  stddev: number;
  n: number;
  runsRedacted: true;        // explicit marker: absence-of-runs is redaction, NOT an empty set
  recallMetrics?: RecallMetrics;  // aggregate → feed-safe; survives redaction
}

export interface RedactedExamVerdict extends Omit<ExamVerdict, "arms"> {
  arms: RedactedArmAggregate[];
  redacted: true;
}

// ── Task-set consumer contracts — additive; mirror of the on-disk YAML. ──

export type TaskSlice = "dev" | "sealed";
export type TaskShape = "single_shot" | "episode";
export type AssertionPolarity = "must_hold" | "must_not_hold";
export type PairingClass = "impact" | "dream_delta" | "control" | "recall_negative";
export type SignoffStatus = "draft" | "proposed" | "approved" | "retired";
export interface TaskAssertion {
  id: string;
  text: string;
  polarity: AssertionPolarity;
  recallTarget?: string;     // expected brain file path for observe() correlation
  negativeClass?: "interference" | "stale" | "privacy";  // only meaningful on must_not_hold;
                             // interference = stem-paired near-miss (pools into precision);
                             // stale = superseded value (own withhold-rate); privacy = audience-scoped (own withhold-rate)
  oracleTier?: "state_check" | "judge";   // defaults "judge"; tags reducer-derived state-checks
}

// ── seed + consolidation-effect types ──
export interface SeedFile { path: string; content: string; }  // seed set MUST include memory/MEMORY.md
export type ConsolidationEdit =
  | { op: "capture";   path: string; content: string; evidence: string }
  | { op: "supersede"; path: string; content: string; supersedes: string; evidence: string }
  | { op: "retract";   path: string; reason: string; evidence: string };

export interface ExamTask {
  taskId: string; taskSetVersion: string; title: string; slice: TaskSlice; shape: TaskShape;
  examType?: ExamType;   // per-task exam kind ("continuity" | "recall_pr" | "forgetting" | …); absent on older tasks
  skills?: string[];     // which skills this task exercises (abstraction slice selection); absent on older tasks
  probe?: { turn: string };
  episode?: {
    seedTurns: { role: "user" | "assistant"; text: string }[];
    consolidate: boolean;
    probeTurns: { role: "user" | "assistant"; text: string }[];
    consolidationEffect?: ConsolidationEdit[];  // declared edits for manual consolidate()
  };
  seed?: { files: SeedFile[] };           // seed content materialized into the arm
  armsApplicability: { requires: string[]; appliesTo: string[]; pairingClass: PairingClass };
  assertions: TaskAssertion[];
  provenance: { source: "transcript" | "spike_probe" | "synthetic"; sourceRefs: string[]; derivedBy: string; contentFilterChecked: boolean };
  signoff: { status: SignoffStatus; approvedBy?: string; approvedAt?: string; approvalRef?: string };
}
export interface TaskSetManifest {
  taskSetVersion: string; worker: string; createdAt: string; basedOnSpike?: string;
  slices: { dev: string[]; sealed: string[] };           // sealed: ids only
  signoffRollup: { approved: number; pending: number };
  calibrationTargets?: { taskId: string; spikeProbe: string; expectedFlip: string }[];
}

/** The single exam grader-prose constant. Distinct from the gate's `f1.x` line.
 *  Bump on ANY grader-prose change (criteria text, input builders, suite-critique prompt,
 *  parser semantics); a bump resets the exam-side validation window. Pinned into
 *  ExamVerdict.evalVersion on every verdict. */
export const EXAM_EVAL_VERSION = "exam-f2.0";

/**
 * The ONLY permitted producer of an E1-facing feed from a full ExamVerdict.
 * Pure + total. Sealed-task runs collapse to aggregates; dev-only verdicts pass through
 * UNCHANGED (identity). Strips ALL sealed-task TEXT (assertions, evidence, raw turns,
 * implicitClaims, the runs[] array) AND drops any suiteCritique entry attributed to a
 * sealed taskId — BY ID, not by substring-matching the taskId inside the text. A grader
 * FEEDBACK line quotes the sealed assertion TEXT and may name no taskId at all, so a
 * substring scan leaks it; id-based attribution (set on the entry at the source) closes
 * the leak (the Law-1 sealed-text fix). KEEPS the version provenance
 * (rubricVersion / evalVersion / loaderSha / judgeDeployment) — they name THAT something
 * changed, not WHAT, and are public-safe. comparisons[].flips keep
 * direction + taskId so per-task flip counts survive for the sign test.
 * Returns RedactedExamVerdict iff ANY arm contains a sealed task, else the verdict unchanged.
 */
export function redactForFeed(
  verdict: ExamVerdict,
  sealedTaskIds: Set<string>,
): ExamVerdict | RedactedExamVerdict {
  // Redact if ANY sealed reference exists — a sealed run in an arm OR a sealed-attributed suiteCritique
  // entry. The latter guard means a sealed critique can never slip through via the identity path.
  const hasSealedRun = verdict.arms.some((a) => a.runs.some((r) => sealedTaskIds.has(r.taskId)));
  const hasSealedCritique = verdict.suiteCritique.some((c) => sealedTaskIds.has(c.taskId));
  if (!hasSealedRun && !hasSealedCritique) return verdict; // dev-only -> identity pass-through (total + cheap)

  const redactedArms: RedactedArmAggregate[] = verdict.arms.map((a) => {
    const ra: RedactedArmAggregate = {
      arm: a.arm,
      passRate: a.passRate,
      stddev: a.stddev,
      n: a.n,
      runsRedacted: true,
    };
    if (a.recallMetrics !== undefined) ra.recallMetrics = a.recallMetrics;  // aggregate → feed-safe
    return ra;
  });

  // Drop any suiteCritique entry attributed to a sealed task — BY ID (not substring): the entry's
  // `text` may quote the sealed assertion verbatim and name no taskId, so id attribution is the
  // only sound drop (the Law-1 sealed-text leak fix).
  const cleanedCritique = verdict.suiteCritique.filter((c) => !sealedTaskIds.has(c.taskId));

  const { arms: _arms, suiteCritique: _sc, ...rest } = verdict;
  return {
    ...rest,
    arms: redactedArms,
    suiteCritique: cleanedCritique,
    redacted: true,
  };
}

/**
 * The referee-FEED projection of a full ExamVerdict.
 *
 * RATIONALE: the FEED (the RefereeFeed Table + the snapshot repo) carries AGGREGATES
 * ONLY — per-run detail (assertions, evidence, implicitClaims, costs-per-run) lives in
 * the referee ANNEX blob, written before any feed attempt. This is deliberately
 * STRICTER than the on-repo feed.json convention guarded by
 * scripts/check-referee-secrecy.mjs (which still allows dev runs[] in files — that
 * guard is unchanged). Without this, a dev-only verdict passes through redactForFeed
 * as an IDENTITY, keeps arms[].runs[], and every feed.append fails the
 * assertFeedEntrySafe fail-closed check — a deterministic failure the publish path
 * would otherwise park-and-retry forever.
 *
 * Semantics: redactForFeed first (sealed-task redaction + sealed-attributed
 * suiteCritique drop, unchanged — the CLI file path still uses redactForFeed
 * directly), then strip `runs` from EVERY arm — dev arms included — keeping
 * {arm, passRate, stddev, n, recallMetrics?} and marking each stripped arm
 * runsRedacted:true; the result always carries redacted:true. Pure + total.
 */
export function projectVerdictForFeed(
  verdict: ExamVerdict,
  sealedTaskIds: Set<string>,
): RedactedExamVerdict {
  const base = redactForFeed(verdict, sealedTaskIds);
  if ("redacted" in base) {
    // Sealed path: redactForFeed already stripped every arm's runs[] and dropped
    // sealed critique entries — nothing left to project.
    return base;
  }

  // Dev-only identity path: strip runs from every arm anyway (aggregates only).
  const full = base;
  const arms: RedactedArmAggregate[] = full.arms.map((a) => {
    const ra: RedactedArmAggregate = {
      arm: a.arm,
      passRate: a.passRate,
      stddev: a.stddev,
      n: a.n,
      runsRedacted: true,
    };
    if (a.recallMetrics !== undefined) ra.recallMetrics = a.recallMetrics; // aggregate → feed-safe
    return ra;
  });
  const { arms: _arms, ...rest } = full;
  return { ...rest, arms, redacted: true };
}

// ── Referee-as-a-service feed contracts — additive. ──

/** Per-MTok USD rates for a single deployment (worker or judge model). */
export interface ModelRates {
  promptUsdPerMTok: number;
  completionUsdPerMTok: number;
}

/** Aggregate run cost across every run in a verdict (all arms, all reps). Pure roll-up of
 *  RunResult.cost — never re-derives tokens, only sums what the runs already measured.
 *  `totalTokens`/`totalPromptTokens`/`totalCompletionTokens`/`usdEstimate` go null the moment
 *  ANY run in the verdict is unmeasured (RunCost.tokens === null for that run) — a partial sum
 *  would misrepresent the true cost, so the honest answer is "unknown", not "undercounted".
 *  `totalDurationMs` is always summable (RunCost.durationMs is always measurable client-side). */
export interface CostSummary {
  totalTokens: number | null;
  totalPromptTokens: number | null;
  totalCompletionTokens: number | null;
  totalDurationMs: number;
  usdEstimate: number | null;
}

/**
 * Sum RunResult.cost across every arm/run in `verdict` into a CostSummary. Pure + total.
 * `totalDurationMs` always sums (durationMs is never null). The token totals — and therefore
 * `usdEstimate` — collapse to null the instant any single run carries `tokens: null`; a partial
 * token sum would silently understate cost, so this refuses to guess.
 * `usdEstimate` is null whenever tokens are null OR no `rates.worker` was supplied (rates are
 * opt-in — callers without a rate table get token counts but no dollar figure). When worker
 * tokens ARE measurable and `rates.worker` is supplied, `rates.judgeUsd` (if given) is added
 * on top of the worker-token cost — judge cost is a flat per-run addend, not derived from
 * tokens. `judgeUsd` alone (tokens null or no worker rates) never produces a non-null estimate:
 * the judge cost alone does not represent the full run cost.
 */
export function computeCostSummary(
  verdict: ExamVerdict,
  rates?: { worker?: ModelRates; judgeUsd?: number },
): CostSummary {
  let totalDurationMs = 0;
  let totalTokens = 0;
  let totalPromptTokens = 0;
  let totalCompletionTokens = 0;
  let anyUnmeasured = false;
  let anyPromptUnmeasured = false;
  let anyCompletionUnmeasured = false;

  for (const arm of verdict.arms) {
    for (const r of arm.runs) {
      totalDurationMs += r.cost.durationMs;

      if (r.cost.tokens === null) anyUnmeasured = true;
      else totalTokens += r.cost.tokens;

      const p = r.cost.promptTokens;
      if (p === null || p === undefined) anyPromptUnmeasured = true;
      else totalPromptTokens += p;

      const c = r.cost.completionTokens;
      if (c === null || c === undefined) anyCompletionUnmeasured = true;
      else totalCompletionTokens += c;
    }
  }

  const tokensKnown = !anyUnmeasured;
  const promptKnown = !anyPromptUnmeasured;
  const completionKnown = !anyCompletionUnmeasured;

  const workerRates = rates?.worker;
  const usdEstimate =
    tokensKnown && promptKnown && completionKnown && workerRates
      ? (totalPromptTokens / 1_000_000) * workerRates.promptUsdPerMTok +
        (totalCompletionTokens / 1_000_000) * workerRates.completionUsdPerMTok +
        (rates.judgeUsd ?? 0)
      : null;

  return {
    totalTokens: tokensKnown ? totalTokens : null,
    totalPromptTokens: promptKnown ? totalPromptTokens : null,
    totalCompletionTokens: completionKnown ? totalCompletionTokens : null,
    totalDurationMs,
    usdEstimate,
  };
}

/** What caused this feed entry to be written. */
export type RefereeTrigger = "scheduled" | "promotion" | "manual" | "skipped";

/**
 * One entry in the E1-facing referee feed. `verdict` is ALWAYS the
 * `projectVerdictForFeed` output (or null on a skipped heartbeat) — never a raw ExamVerdict.
 * `projectVerdictForFeed` runs `redactForFeed` first (sealed-task redaction) and then strips
 * `runs[]` from EVERY arm, dev included — the feed carries aggregates only; per-run detail
 * lives in the annex. `costSummary` is present on every entry, including skipped ones
 * (all-zero/$0), so cost can be tallied across the whole feed uniformly without a null-check
 * per entry.
 */
export interface RefereeFeedEntry {
  brain: string;
  runId: string;
  trigger: RefereeTrigger;
  examType: ExamType | null;
  verdict: ExamVerdict | RedactedExamVerdict | null;
  skipReason?: string;
  brainSha: string | null;
  costSummary: CostSummary;
  promotion?: { repo: string; prNumber: number; skillSlug: string; baseSha: string; mergeSha: string };
  diagnostics?: { reAskRate?: { value: number; nUserTurns: number; method: "lexical-v1"; diagnostic: true } };
  createdAt: string;
}

// Keys that must never appear anywhere inside a feed entry's `verdict` — sealed body content.
// Mirrors scripts/check-referee-secrecy.mjs:checkFeedContent, plus the
// stricter additions (assertions/evidence/implicitClaims: a `runs`
// array is already forbidden below, but scanning for these directly makes the guard robust to
// any future shape that hoists sealed text outside of runs[]).
const FORBIDDEN_KEYS = [
  "runs",
  "readDiagnostics",
  "recallTarget",
  "assertions",
  "evidence",
  "implicitClaims",
] as const;

function hasKeyDeep(node: unknown, key: string): boolean {
  if (node === null || typeof node !== "object") return false;
  if (Array.isArray(node)) return node.some((item) => hasKeyDeep(item, key));
  if (Object.prototype.hasOwnProperty.call(node, key)) return true;
  return Object.values(node as Record<string, unknown>).some((v) => hasKeyDeep(v, key));
}

/**
 * Structural secrecy check for a single RefereeFeedEntry (TS port of
 * scripts/check-referee-secrecy.mjs:checkFeedContent, STRICTER — see FORBIDDEN_KEYS). Pure + total.
 * Returns an empty array when the entry is safe; otherwise a list of human-readable violations.
 *
 * Unlike the .mjs script (which only runs its content check when the worker's manifest declares
 * sealed tasks), this function has no manifest to consult — it is a pure function of the entry
 * itself. It therefore applies a strictly-necessary condition: if `verdict` is non-null, either
 * it is already marked `redacted: true` (the redactForFeed output for a verdict that touched a
 * sealed task), OR it must carry none of the FORBIDDEN_KEYS anywhere in its tree (the
 * `redactForFeed` identity pass-through for a dev-only verdict, which legitimately carries
 * `runs[]`... but a dev-only runs[] never contains sealed-shaped keys like recallTarget content
 * from a sealed task, since dev-only verdicts hold no sealed tasks by definition). A verdict that
 * is NOT marked redacted:true but DOES carry any forbidden key is flagged — the sealed-content
 * leak the guard exists to catch.
 */
export function assertFeedEntrySafe(entry: RefereeFeedEntry): string[] {
  const violations: string[] = [];
  const verdict = entry.verdict;

  // Scan the ENTIRE entry (not just verdict) — a forbidden key hoisted onto a sibling
  // top-level field (e.g. diagnostics, promotion) is just as much a leak as one nested
  // inside verdict itself, and the guard must not miss it. This runs regardless of whether
  // `verdict` itself is null (a skipped-heartbeat entry can still smuggle a forbidden key
  // via another field), but the redacted-marker check below still only applies to `verdict`.
  const isMarkedRedacted = verdict !== null && (verdict as { redacted?: boolean }).redacted === true;
  const foundKeys = FORBIDDEN_KEYS.filter((key) => hasKeyDeep(entry, key));

  if (foundKeys.length > 0 && !isMarkedRedacted) {
    violations.push(
      `entry ${entry.runId}: verdict contains forbidden key(s) [${foundKeys.join(", ")}] but is not marked redacted:true — ` +
        `redactForFeed did not run or was bypassed`,
    );
  } else if (foundKeys.length > 0 && isMarkedRedacted) {
    // Defense-in-depth: even a verdict CLAIMING redacted:true must not carry these keys —
    // a redacted:true verdict with a leaked forbidden key anywhere is still a leak.
    violations.push(
      `entry ${entry.runId}: verdict is marked redacted:true but still contains forbidden key(s) [${foundKeys.join(", ")}] — ` +
        `sealed content leaked past redactForFeed`,
    );
  }

  return violations;
}
